import Button from '@rio-cloud/rio-uikit/Button';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import type { MaterialType } from '#domain/Material.ts';
import type { ImportOrderFormData } from '#features/csvImport/ImportOrderFormValidation.ts';

import { CargoDimensionFields, CargoLoadMetricFields } from './CargoMaterialFields.tsx';
import { LoadForm } from './LoadForm.tsx';
import type { MaterialFormData, OrderFormData } from './OrderFormValidation.ts';

type FreightFormProps = {
    index: number;
    name: `orders.${number}.materials` | 'materials';
    onPackageTypeSelect?: (packageType: string, type: MaterialType) => boolean;
};

export const FreightForm = ({ index, name, onPackageTypeSelect }: FreightFormProps) => {
    const { control } = useFormContext<OrderFormData | ImportOrderFormData>();

    const materialPrefix = `${name}.${index}` as const;

    const material: MaterialFormData = useWatch({
        control,
        name: materialPrefix, // Name des Array-Feldes
    });

    const isCargoMaterial = material?.type === 'cargo-material';
    const hasDimensions =
        isCargoMaterial && [material.length, material.width, material.height].some(value => value !== undefined);
    const [showDimensions, setShowDimensions] = useState(hasDimensions);
    const dimensionsId = `${materialPrefix.replaceAll('.', '-')}-dimensions`;

    if (!material) {
        return null;
    }

    return (
        <div className='bg-lightest rounded border padding-15'>
            <LoadForm
                index={index}
                materialPrefix={materialPrefix}
                materialType={material.type}
                onPackageTypeSelect={onPackageTypeSelect}
            />
            {isCargoMaterial && (
                <div>
                    <CargoLoadMetricFields materialPrefix={materialPrefix} />
                    <Button
                        aria-controls={dimensionsId}
                        aria-expanded={showDimensions}
                        autoTrackingKey={`ordercom.orders.create-dialog.${
                            showDimensions ? 'hide-dimensions' : 'show-dimensions'
                        }`}
                        bsStyle='primary'
                        onClick={() => setShowDimensions(value => !value)}
                        variant='link-inline'
                    >
                        <FormattedMessage
                            id={
                                showDimensions
                                    ? 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.hide-dimensions'
                                    : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.show-dimensions'
                            }
                        />
                    </Button>
                    {showDimensions && (
                        <div id={dimensionsId}>
                            <hr className='margin-top-15 margin-bottom-10' />
                            <CargoDimensionFields materialPrefix={materialPrefix} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
